import React, { useState, useRef, MutableRefObject, useEffect } from "react";
import NextImage from "next/image";
import Script from "next/script";
import dynamic from "next/dynamic";

const LottieLoader = dynamic(
  () =>
    import("@/components/Molecules/FullScreenLoader").then(
      (module) => module.LottieLoader
    ),
  { ssr: false }
);

import SVGSprite from "@/components/Atoms/SVGSprite";
import Button from "@/components/Atoms/Button";

import { clsx, getURLParams } from "@/utils";
import Typography from "@/foundation/Typography/V2";
import theme from "@/foundation/Theme";

import styles from "./CommunityJoiningPage.module.scss";

import data from "./data";

interface JoinButtonProps {
  className: string;
  headerCTAText: string;
  formRef: MutableRefObject<HTMLDivElement>;
}

const JoinButton: React.FC<JoinButtonProps> = ({
  className,
  headerCTAText,
  formRef,
}) => (
  <div className={clsx(styles.joinButton, "join-button", className)}>
    <Button
      onClick={() =>
        formRef?.current?.scrollIntoView({
          behavior: "smooth",
          block: "start",
        })
      }
    >
      {headerCTAText}
      <SVGSprite
        icon="icon-arrow-up-right-join-button"
        height="20px"
        width="20px"
        style={{ marginLeft: "8px" }}
      />
    </Button>
  </div>
);

interface HowItWorksI {
  icon: string;
  title: string;
  description?: string;
}

interface FormDataI {
  src: string;
  title: string;
}

interface JoinCommunityPageProps {
  h1Tag: string;
  headerCTAText: string;
  heading: string;
  subheading: string;
  welcomePrefix: string;
  welcomeHeading: string;
  welcomeSubheading: string;
  howItWorksTitle: string;
  howItWorks: HowItWorksI[];
  glow1Url: string;
  glow2Url: string;
  formData: FormDataI;
}

const CommunityJoiningPage: React.FC = () => {
  const {
    h1Tag,
    headerCTAText,
    heading,
    subheading,
    welcomePrefix,
    welcomeHeading,
    welcomeSubheading,
    howItWorksTitle,
    howItWorks,
    glow1Url,
    glow2Url,
    formData,
  } = data as JoinCommunityPageProps;

  const formRef = useRef() as MutableRefObject<HTMLDivElement>;
  const tallyFormRef = useRef() as MutableRefObject<HTMLDivElement>;

  const [isFormLoading, setIsFormLoading] = useState(true);

  useEffect(() => {
    const params = getURLParams();
    const utm_source = params?.get("utm_source") || "";

    if (!isFormLoading && utm_source?.toLowerCase() === "email") {
      setTimeout(() => {
        tallyFormRef?.current?.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }, 1000);
    }
  }, [isFormLoading, tallyFormRef]);

  return (
    <>
      <Script
        src="https://tally.so/widgets/embed.js"
        onReady={() => {
          window?.Tally?.loadEmbeds();
          setIsFormLoading(false);
        }}
      />
      <div className={styles.joinCommunityPageWrapper}>
        <div className={styles.joinCommunityPage}>
          <div className={clsx(styles.header, "container")}>
            <div className={styles.logo}>
              <Typography variant="routelink1" to="/">
                <SVGSprite
                  icon="icon-brand-logo-light"
                  fill={theme.colors.brand_logo}
                />
              </Typography>
            </div>
            <JoinButton
              className="join-button-header"
              headerCTAText={headerCTAText}
              formRef={formRef}
            />
          </div>
          <div className={clsx(styles.content, "container")}>
            <div className={styles.heading}>{heading}</div>
            <div className={styles.subheading}>{subheading}</div>
            <div className={styles.welcomePrefix}>{welcomePrefix}</div>
            <h1 style={{ display: "none" }}>{h1Tag}</h1>
            <div className={styles.welcomeHeading}>{welcomeHeading}</div>
            <div className={styles.welcomeSubheading}>{welcomeSubheading}</div>
          </div>
          <div className={styles.howItWorksSectionWrapper}>
            <div className={clsx(styles.howItWorksSection, "container")}>
              <div className={styles.howItWorksTitle}>{howItWorksTitle}</div>
              <div className={styles.howItWorks}>
                {howItWorks?.map(({ icon, title, description }, index) => (
                  <div key={index} className={styles.howItWork}>
                    <div className={styles.icon}>
                      <SVGSprite icon={icon} />
                    </div>
                    <div className={styles.details}>
                      <div className={styles.title}>{title}</div>
                      {description && (
                        <div className={styles.description}>{description}</div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
              <div className={styles.joinButtonWrapper}>
                <JoinButton
                  className="join-button-how-it-works"
                  headerCTAText={headerCTAText}
                  formRef={formRef}
                />
              </div>
            </div>
          </div>
        </div>
        <div className={styles.formWrapper}>
          <div
            className={clsx(styles.formContainer, "container")}
            ref={formRef}
          >
            <div className={styles.glow1}>
              <NextImage src={glow1Url} height="440" width="440" alt="glow-1" />
            </div>
            <div className={styles.glow2}>
              <NextImage src={glow2Url} height="440" width="440" alt="glow-2" />
            </div>

            {isFormLoading && (
              <div className={styles.loader}>
                <LottieLoader />
              </div>
            )}
            <div className={styles.tallyForm} ref={tallyFormRef}>
              <iframe
                data-tally-src={formData.src}
                loading="eager"
                width="100%"
                height="0"
                frameBorder="0"
                marginHeight={0}
                marginWidth={0}
                title={formData.title}
              />
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default CommunityJoiningPage;
